import React, { useEffect, useState } from "react";
import { Clock, AlertTriangle, RefreshCw, Phone, Wallet, CheckCircle2, Search } from "lucide-react";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { toast } from "sonner";
import { formatINR } from "../../lib/utils.fmt";
import EmptyState from "../../components/EmptyState";

export default function AdminTrials() {
  const [data, setData] = useState({ rows: [], stats: {} });
  const [busy, setBusy] = useState(false);
  const [trialDays, setTrialDays] = useState(null);
  const [q, setQ] = useState("");

  const load = async () => {
    setBusy(true);
    try {
      const [t, me] = await Promise.all([
        api.get("/api/admin/trials/on-trial").then((r) => r.data),
        api.get("/api/admin/tenant/me").then((r) => r.data).catch(() => null),
      ]);
      setData(t);
      if (me?.tenant) setTrialDays(me.tenant.trial_days_for_subscriptions ?? 3);
    } finally { setBusy(false); }
  };
  useEffect(() => { load(); }, []);

  const endTrial = async (subId, name) => {
    if (!window.confirm(`End ${name}'s free trial now? Future deliveries will debit the wallet.`)) return;
    try {
      await api.post(`/api/admin/trials/${subId}/end`);
      toast.success("Trial ended");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const stats = data.stats || {};
  return (
    <div className="space-y-6 page-enter" data-testid="admin-trials-page">
      <div>
        <div className="text-xs uppercase tracking-wider font-bold text-neutral-500">Customers</div>
        <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight flex items-center gap-2">
          <Clock className="w-7 h-7 text-brand-sand"/> On Trial
        </h1>
        <p className="text-sm text-neutral-600 mt-1">
          Customers currently enjoying their free-delivery trial. Follow up before the trial ends to convert them to monthly subscriptions.
          {trialDays === 0 && (
            <span className="ml-1 text-brand-clay font-bold">Trial is currently disabled in <a href="/admin/settings" className="underline">Settings</a>.</span>
          )}
        </p>
      </div>

      <section className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="trials-stats">
        <Stat label="On trial" value={stats.total ?? 0} icon={Clock}/>
        <Stat label="Active" value={stats.active ?? 0} icon={CheckCircle2}/>
        <Stat label="Expiring today" value={stats.expiring_today ?? 0} icon={AlertTriangle}/>
        <Stat label="Expired" value={stats.expired ?? 0} icon={AlertTriangle}/>
      </section>

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h2 className="font-heading text-lg font-extrabold tracking-tight">All trial customers</h2>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400"/>
            <Input placeholder="Search name, phone, product…" className="pl-10 h-9 rounded-2xl w-56 sm:w-64" value={q} onChange={(e) => setQ(e.target.value)} data-testid="trials-search-input"/>
          </div>
          <Button onClick={load} disabled={busy} variant="ghost" className="h-9 rounded-2xl text-xs" data-testid="refresh-trials">
            <RefreshCw className={`w-3.5 h-3.5 mr-1 ${busy ? "animate-spin" : ""}`}/> Refresh
          </Button>
        </div>
      </div>

      <div className="bg-white rounded-3xl border border-neutral-200 overflow-hidden">
        {data.rows.length === 0 ? (
          <EmptyState icon={Clock} title="No trial customers right now"
                      description="New first-time subscribers will appear here automatically."
                      testId="trials-empty" />
        ) : (
          <table className="w-full">
            <thead className="bg-neutral-50 text-xs uppercase tracking-wider font-bold text-neutral-500">
              <tr>
                <th className="text-left px-5 py-3">Customer</th>
                <th className="text-left px-5 py-3">Subscription</th>
                <th className="text-left px-5 py-3">Billing</th>
                <th className="text-left px-5 py-3">Trial</th>
                <th className="text-right px-5 py-3">Wallet</th>
                <th className="text-right px-5 py-3">Top-up needed</th>
                <th className="text-right px-5 py-3">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {data.rows.filter((r) => !q.trim() || (r.customer_name || "").toLowerCase().includes(q.toLowerCase()) || (r.customer_phone || "").includes(q) || (r.product_name || "").toLowerCase().includes(q.toLowerCase())).map((r) => {
                const tone = r.expired ? "text-brand-clay" : r.days_left === 0 ? "text-brand-sand" : "text-brand-leaf";
                const isFree = (r.trial_billing || "free") === "free";
                return (
                  <tr key={r.subscription_id} data-testid={`trial-row-${r.subscription_id}`}>
                    <td className="px-5 py-3">
                      <div className="font-bold">{r.customer_name || "—"}</div>
                      <div className="text-xs text-neutral-500 inline-flex items-center gap-1">
                        <Phone className="w-3 h-3"/> {r.customer_phone}
                      </div>
                    </td>
                    <td className="px-5 py-3 text-sm">{r.product_name || "—"}</td>
                    <td className="px-5 py-3 text-xs">
                      <span className={`px-2 py-0.5 rounded-full font-extrabold uppercase tracking-wider ${isFree ? "bg-brand-leaf/15 text-brand-leaf" : "bg-brand-sky/15 text-brand-sky"}`}>
                        {isFree ? "Free" : "Paid"}
                      </span>
                    </td>
                    <td className={`px-5 py-3 text-sm ${tone} font-bold`}>
                      {r.expired ? `Expired ${r.trial_expires_at}` :
                       r.days_left === 0 ? "Last day · expires today" :
                       `${r.days_left} day${r.days_left === 1 ? "" : "s"} left`}
                      <div className="text-[11px] text-neutral-500 font-normal">
                        {r.trial_notified_at ? "Push sent ✓" : "Push pending"}
                      </div>
                    </td>
                    <td className="px-5 py-3 text-right font-mono text-sm">
                      <Wallet className="w-3.5 h-3.5 inline -mt-0.5 mr-0.5 text-neutral-400"/>
                      {formatINR(r.wallet_balance_inr)}
                    </td>
                    <td className="px-5 py-3 text-right">
                      <div className="font-mono font-extrabold text-brand-clay">
                        {r.needed_topup_inr > 0 ? formatINR(r.needed_topup_inr) : "—"}
                      </div>
                      <div className="text-[10px] text-neutral-500">
                        {formatINR(r.topup_target_inr)} / mo plan
                      </div>
                    </td>
                    <td className="px-5 py-3 text-right">
                      <Button onClick={() => endTrial(r.subscription_id, r.customer_name)}
                              variant="outline"
                              className="h-9 rounded-2xl text-xs font-bold"
                              data-testid={`end-trial-${r.subscription_id}`}>
                        End trial
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, icon: Icon }) {
  return (
    <div className="bg-white rounded-2xl border border-neutral-200 p-4">
      <div className="flex items-center gap-2 text-xs uppercase tracking-wider font-bold text-neutral-500">
        <Icon className="w-3.5 h-3.5"/> {label}
      </div>
      <div className="font-heading text-2xl font-extrabold mt-1">{value}</div>
    </div>
  );
}
